import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout, { Loading } from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Pipeline from "./pages/Pipeline";
import MyQueue from "./pages/MyQueue";
import AllocationQueue from "./pages/AllocationQueue";
import Accounts from "./pages/Accounts";
import AccountDetail from "./pages/AccountDetail";
import LeadDetail from "./pages/LeadDetail";
import Regions from "./pages/Regions";
import Spend from "./pages/Spend";
import ChangeLog from "./pages/ChangeLog";
import Settings from "./pages/Settings";
import ApiKeys from "./pages/ApiKeys";
import Jarvis from "./pages/Jarvis";
import BulkImport from "./pages/BulkImport";
import Opportunities from "./pages/Opportunities";
import Coverage from "./pages/Coverage";
import KnowledgeBase from "./pages/KnowledgeBase";
import LinkedInAgent from "./pages/LinkedInAgent";
import Productivity from "./pages/Productivity";
import NurtureCalendar from "./pages/NurtureCalendar";
import UserManagement from "./pages/UserManagement";
import Offboarding from "./pages/Offboarding";
import Vendors from "./pages/Vendors";
import Network from "./pages/Network";
import Targets from "./pages/Targets";
import AdminRecovery from "./pages/AdminRecovery";
import ResetPassword from "./pages/ResetPassword";
import SetPassword from "./pages/SetPassword";
import SuperAdmin from "./pages/SuperAdmin";
import Organization from "./pages/Organization";
import Approvals from "./pages/Approvals";
import Help from "./pages/Help";
import Mail from "./pages/Mail";
import Offerings from "./pages/Offerings";
import Onboarding from "./pages/Onboarding";
import ThemeLoader from "./components/ThemeLoader";
import { ThemeProvider } from "./context/ThemeContext";
import { PwaProvider } from "./components/PwaInstall";

function Protected({ children, roles, bare }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center"><Loading /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin" && !(roles && roles.includes("super_admin")))
    return <Navigate to="/admin/tenants" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return bare ? children : <Layout>{children}</Layout>;
}

function Router() {
  const { user, ready } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={ready && user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/admin-recovery" element={<AdminRecovery />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
      <Route path="/queue" element={<Protected roles={["rep"]}><MyQueue /></Protected>} />
      <Route path="/allocation" element={<Protected roles={["admin", "supervisor"]}><AllocationQueue /></Protected>} />
      <Route path="/accounts" element={<Protected><Accounts /></Protected>} />
      <Route path="/accounts/:id" element={<Protected><AccountDetail /></Protected>} />
      <Route path="/leads/:id" element={<Protected><LeadDetail /></Protected>} />
      <Route path="/regions" element={<Protected roles={["admin", "supervisor"]}><Regions /></Protected>} />
      <Route path="/spend" element={<Protected roles={["admin"]}><Spend /></Protected>} />
      <Route path="/audit" element={<Protected roles={["admin"]}><ChangeLog /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/mail" element={<Protected roles={["admin", "supervisor", "rep"]}><Mail /></Protected>} />
      <Route path="/import" element={<Protected><BulkImport /></Protected>} />
      <Route path="/opportunities" element={<Protected roles={["admin", "supervisor"]}><Opportunities /></Protected>} />
      <Route path="/coverage" element={<Protected roles={["admin", "supervisor"]}><Coverage /></Protected>} />
      <Route path="/knowledge" element={<Protected roles={["admin", "supervisor"]}><KnowledgeBase /></Protected>} />
      <Route path="/linkedin" element={<Protected><LinkedInAgent /></Protected>} />
      <Route path="/productivity" element={<Protected><Productivity /></Protected>} />
      <Route path="/nurture" element={<Protected><NurtureCalendar /></Protected>} />
      <Route path="/users" element={<Protected roles={["admin", "supervisor"]}><UserManagement /></Protected>} />
      <Route path="/organization" element={<Protected roles={["admin"]}><Organization /></Protected>} />
      <Route path="/integrations/api-keys" element={<Protected roles={["admin"]}><ApiKeys /></Protected>} />
      <Route path="/integrations/jarvis" element={<Protected roles={["admin"]}><Jarvis /></Protected>} />
      <Route path="/approvals" element={<Protected roles={["admin", "supervisor", "rep"]}><Approvals /></Protected>} />
      <Route path="/offboarding" element={<Protected roles={["admin", "supervisor"]}><Offboarding /></Protected>} />
      <Route path="/help" element={<Protected roles={["admin", "supervisor", "rep", "super_admin"]}><Help /></Protected>} />
      <Route path="/offerings" element={<Protected roles={["admin", "supervisor"]}><Offerings /></Protected>} />
      <Route path="/onboarding" element={<Protected roles={["admin"]} bare><Onboarding /></Protected>} />
      <Route path="/vendors" element={<Protected><Vendors /></Protected>} />
      <Route path="/network" element={<Protected><Network /></Protected>} />
      <Route path="/targets" element={<Protected><Targets /></Protected>} />
      <Route path="/admin/tenants" element={<Protected roles={["super_admin"]}><SuperAdmin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
        <PwaProvider>
        <ThemeLoader />
        <BrowserRouter>
          <Router />
        </BrowserRouter>
        <Toaster position="top-right" />
        </PwaProvider>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}
